import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Building2,
  Users,
  Phone,
  Mail,
  MapPin,
  Plus,
  Edit,
  Save,
  X,
  Trash2,
  User,
  Loader2,
  Search,
  Globe,
  Briefcase,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";


export default function MyCompanyContactsPage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  
  // Edit states
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({});
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    position: "",
    department: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCompanyData();
  }, [token]);

  useEffect(() => {
    filterContacts();
  }, [contacts, searchQuery, selectedDepartment]);

  const fetchCompanyData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-company`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(response.data.company);
      const contactsData = response.data.contacts || [];
      setContacts(contactsData);
      setCompanyForm(response.data.company || {});
      
      // Extract departments
      const depts = [...new Set(contactsData.map((c) => c.department || c.position).filter(Boolean))];
      setDepartments(depts);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load company data");
    } finally {
      setLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = [...contacts];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.first_name?.toLowerCase().includes(query) ||
          c.last_name?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query) ||
          c.position?.toLowerCase().includes(query) ||
          c.department?.toLowerCase().includes(query)
      );
    }
    
    if (selectedDepartment !== "all") {
      filtered = filtered.filter(
        (c) => (c.department || c.position) === selectedDepartment
      );
    }
    
    setFilteredContacts(filtered);
  };

  const saveCompanyDetails = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/customer-portal/my-company`,
        companyForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompany(companyForm);
      setEditingCompany(false);
      toast.success("Company details updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update company");
    } finally {
      setSaving(false);
    }
  };

  const openAddContact = () => {
    setContactForm({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      position: "",
      department: "",
    });
    setEditingContact(null);
    setShowAddContact(true);
  };

  const openEditContact = (contact) => {
    setContactForm({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      position: contact.position || "",
      department: contact.department || "",
    });
    setEditingContact(contact);
    setShowAddContact(true);
  };

  const saveContact = async () => {
    if (!contactForm.first_name || !contactForm.email) {
      toast.error("First name and email are required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingContact) {
        await axios.put(
          `${API_URL}/customer-portal/my-company/contacts/${editingContact.id}`,
          contactForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Contact updated");
      } else {
        await axios.post(
          `${API_URL}/customer-portal/my-company/contacts`,
          contactForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Contact added");
      }
      setShowAddContact(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) return;
    
    try {
      await axios.delete(
        `${API_URL}/customer-portal/my-company/contacts/${contactId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contact deleted");
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete contact");
    }
  };

  // Group contacts by department/position
  const groupedContacts = filteredContacts.reduce((acc, c) => {
    const dept = c.department || c.position || "General";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(c);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-[#003366] rounded-2xl mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">My Company & Contacts</h1>
          <p className="text-slate-600 mt-2">
            Manage your company information and team contacts
          </p>
        </div>

        {/* Company Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#003366] to-[#004488] rounded-xl p-6 mb-8 text-white"
        >
          {editingCompany ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-blue-100">Company Name</Label>
                  <Input
                    value={companyForm.name || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <Label className="text-blue-100">Website</Label>
                  <Input
                    value={companyForm.website || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, website: e.target.value })}
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <Label className="text-blue-100">Phone</Label>
                  <Input
                    value={companyForm.phone || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
                    placeholder="+386..."
                  />
                </div>
                <div>
                  <Label className="text-blue-100">Email</Label>
                  <Input
                    value={companyForm.email || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, email: e.target.value })}
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
                    placeholder="info@company.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-blue-100">Address</Label>
                  <Input
                    value={companyForm.address || ""}
                    onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })}
                    className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
                    placeholder="Street, City, Country"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingCompany(false);
                    setCompanyForm(company || {});
                  }}
                  className="text-white hover:bg-white/10"
                >
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={saveCompanyDetails}
                  disabled={saving}
                  className="bg-white text-[#003366] hover:bg-blue-50"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-semibold">{company?.name || user?.company_name || "Your Company"}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingCompany(true)}
                  className="text-white hover:bg-white/10"
                >
                  <Edit className="h-4 w-4 mr-1" /> Edit
                </Button>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {(company?.address || user?.company_name) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Address</p>
                      <p className="text-blue-100 text-sm">
                        {company?.address || "Not set"}
                      </p>
                    </div>
                  </div>
                )}
                {company?.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Phone</p>
                      <p className="text-blue-100 text-sm">{company.phone}</p>
                    </div>
                  </div>
                )}
                {company?.email && (
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Email</p>
                      <p className="text-blue-100 text-sm">{company.email}</p>
                    </div>
                  </div>
                )}
                {company?.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Website</p>
                      <a 
                        href={company.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-100 text-sm hover:underline"
                      >
                        {company.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>

        {/* Search & Filter + Add Button */}
        <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search contacts by name, email, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="contact-search"
                />
              </div>
            </div>
            {departments.length > 0 && (
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="h-10 px-3 rounded-md border border-slate-200 text-sm"
                data-testid="department-filter"
              >
                <option value="all">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            )}
            <Button onClick={openAddContact} data-testid="add-contact-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Contact
            </Button>
          </div>
        </div>

        {/* Contacts Directory */}
        {filteredContacts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl border shadow-sm p-12 text-center"
          >
            <Users className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              {contacts.length === 0 ? "No Contacts Yet" : "No Contacts Found"}
            </h3>
            <p className="text-slate-500 mb-4">
              {contacts.length === 0 
                ? "Add your first team contact to get started"
                : "Try a different search term"}
            </p>
            {contacts.length === 0 && (
              <Button onClick={openAddContact}>
                <Plus className="h-4 w-4 mr-1" /> Add First Contact
              </Button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedContacts).sort().map((department, deptIdx) => (
              <motion.div
                key={department}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: deptIdx * 0.1 }}
                className="bg-white rounded-xl border shadow-sm overflow-hidden"
              >
                <div className="bg-slate-50 px-4 py-3 border-b flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-[#003366]" />
                  <h2 className="font-semibold text-[#003366]">{department}</h2>
                  <span className="text-slate-500 text-sm">({groupedContacts[department].length})</span>
                </div>
                <div className="divide-y">
                  {groupedContacts[department].map((contact, idx) => (
                    <motion.div
                      key={contact.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4 hover:bg-slate-50 transition-colors group"
                    >
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#003366] to-[#0055aa] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                          {contact.first_name?.[0]}{contact.last_name?.[0]}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1">
                          <h3 className="font-semibold text-slate-900">
                            {contact.first_name} {contact.last_name}
                          </h3>
                          {contact.position && (
                            <p className="text-sm text-slate-600">{contact.position}</p>
                          )}
                          
                          {/* Contact Details */}
                          <div className="mt-2 flex flex-wrap gap-4 text-sm">
                            {contact.email && (
                              <a
                                href={`mailto:${contact.email}`}
                                className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                              >
                                <Mail className="h-4 w-4" />
                                <span>{contact.email}</span>
                              </a>
                            )}
                            {contact.phone && (
                              <a
                                href={`tel:${contact.phone}`}
                                className="flex items-center gap-1.5 text-slate-600 hover:text-slate-800"
                              >
                                <Phone className="h-4 w-4" />
                                <span>{contact.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditContact(contact)}
                            className="h-8 w-8"
                            data-testid={`edit-contact-${contact.id}`}
                          >
                            <Edit className="h-4 w-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteContact(contact.id)}
                            className="h-8 w-8 hover:bg-red-50"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? "Edit Contact" : "Add New Contact"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={contactForm.first_name}
                  onChange={(e) => setContactForm({ ...contactForm, first_name: e.target.value })}
                  placeholder="John"
                  data-testid="contact-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={contactForm.last_name}
                  onChange={(e) => setContactForm({ ...contactForm, last_name: e.target.value })}
                  placeholder="Doe"
                  data-testid="contact-last-name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                placeholder="john@company.com"
                data-testid="contact-email"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                placeholder="+386 1 234 5678"
                data-testid="contact-phone"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="position">Position/Role</Label>
                <Input
                  id="position"
                  value={contactForm.position}
                  onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                  placeholder="Sales Manager"
                  data-testid="contact-position"
                />
              </div>
              <div>
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={contactForm.department}
                  onChange={(e) => setContactForm({ ...contactForm, department: e.target.value })}
                  placeholder="Sales"
                  data-testid="contact-department"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContact(false)}>
              Cancel
            </Button>
            <Button onClick={saveContact} disabled={saving} data-testid="save-contact-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {editingContact ? "Update" : "Add Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
